import React, { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { History, Pencil, Trash2, Image as ImageIcon } from "lucide-react";

/**
 * Virtualized inventory list — used when items.length exceeds the threshold
 * defined in the Inventory page. Uses @tanstack/react-virtual for windowing.
 *
 * Layout mirrors the table columns so switching between modes feels seamless.
 */
export function VirtualInventoryList({ items, onEdit, onDelete, onHistory, onSelect, height = 600 }) {
  const parentRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56,
    overscan: 8,
  });

  return (
    <div className="relative" data-testid="inventory-virtualized">
      {/* Sticky header to match the row layout */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border grid grid-cols-[56px_120px_1fr_160px_110px_80px_150px_150px_120px] items-center text-left text-[11px] uppercase tracking-wide text-muted-foreground px-4 py-2">
        <div></div>
        <div>SKU</div>
        <div>Name</div>
        <div>Category</div>
        <div className="text-right">Qty</div>
        <div className="text-right">Min</div>
        <div>Location</div>
        <div>Supplier</div>
        <div></div>
      </div>

      <div ref={parentRef} className="max-h-[70vh] overflow-auto" style={{ height: `min(${height}px, 70vh)` }}>
        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const it = items[virtualRow.index];
            const low = it.min_stock > 0 && it.quantity <= it.min_stock;
            return (
              <div
                key={it.id}
                data-testid={`inventory-row-${it.id}`}
                className="absolute left-0 right-0 grid grid-cols-[56px_120px_1fr_160px_110px_80px_150px_150px_120px] items-center text-sm px-4 hover:bg-accent/40 border-b border-border"
                style={{ transform: `translateY(${virtualRow.start}px)`, height: `${virtualRow.size}px` }}
              >
                <div>
                  {it.image_base64 ? (
                    <img src={it.image_base64} alt={it.name} className="w-8 h-8 rounded object-cover border border-border" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted text-muted-foreground flex items-center justify-center">
                      <ImageIcon className="w-3.5 h-3.5" />
                    </div>
                  )}
                </div>
                <div className="font-mono text-xs truncate">
                  {onSelect ? (
                    <button
                      type="button"
                      className="text-left hover:text-primary hover:underline focus:text-primary focus:underline outline-none truncate max-w-full"
                      onClick={() => onSelect(it)}
                      data-testid={`inventory-sku-${it.id}`}
                    >
                      {it.sku}
                    </button>
                  ) : it.sku}
                </div>
                <div className="font-medium truncate">
                  {onSelect ? (
                    <button
                      type="button"
                      className="text-left hover:text-primary hover:underline focus:text-primary focus:underline outline-none truncate max-w-full"
                      onClick={() => onSelect(it)}
                      data-testid={`inventory-name-${it.id}`}
                    >
                      {it.name}
                    </button>
                  ) : it.name}
                </div>
                <div>
                  <span className="inline-flex rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px]">{it.category}</span>
                </div>
                <div className="text-right tabular-nums">
                  <span className={low ? "text-amber-600 dark:text-amber-400 font-medium" : ""}>
                    {it.quantity} {it.unit}
                  </span>
                </div>
                <div className="text-right tabular-nums text-muted-foreground">{it.min_stock || "—"}</div>
                <div className="text-muted-foreground truncate">{it.location || "—"}</div>
                <div className="text-muted-foreground truncate">{it.supplier || "—"}</div>
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onHistory(it)} title="History" data-testid={`inventory-history-${it.id}`}>
                    <History className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(it)} title="Edit" data-testid={`inventory-edit-${it.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete" data-testid={`inventory-delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete item?</AlertDialogTitle>
                        <AlertDialogDescription>This will remove “{it.name}”. Stock movement history remains.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => onDelete(it)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
